import { useEffect, useMemo, useRef, useState } from 'react'
import { FaCalendarAlt, FaChevronDown } from 'react-icons/fa'
import styled from 'styled-components'
import { formatDateTime } from 'utils/date'
import { ActionButton, DateInput, DateInputGroup, DateRangePanelRow } from './DateRangeField'
import { DAY_MS, PRESETS, parseYMDLocal, sanitizeYMD, subtractDaysWindowLocal, ymdLocal } from './dateUtils'

type Mode = 'preset' | 'nDays' | 'range' | null

type LastModifiedQuickProps = {
  label?: string
  onCommit: (p: { model: any | null; text: string }) => void
  displayText?: string
  currentModel?: any | null
}

export function LastModifiedQuick({
  label = 'Last Modified',
  onCommit,
  displayText,
  currentModel,
}: LastModifiedQuickProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  // UI state (only for the panel)
  const [mode, setMode] = useState<Mode>(null)
  const [presetDays, setPresetDays] = useState<number | null>(null)
  const [nDays, setNDays] = useState('')
  const [from, setFrom] = useState('') // YYYY-MM-DD
  const [to, setTo] = useState('') // YYYY-MM-DD

  const todayYMD = useMemo(() => ymdLocal(new Date()), [])
  const externalPreset = currentModel?._metaData?.preset != null ? Number(currentModel._metaData.preset) : null
  const externalCustom = !!currentModel?._metaData?.customRange

  // Initialize panel state from currentModel when opened
  useEffect(() => {
    if (!open) {
      resetPanel()
      return
    }
    hydrateFromCurrentModel()
  }, [open, currentModel])

  // Close on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!hostRef.current?.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // ---------- helpers ----------
  function resetPanel() {
    setMode(null)
    setPresetDays(null)
    setNDays('')
    setFrom('')
    setTo('')
  }

  function hydrateFromCurrentModel() {
    if (!currentModel) return resetPanel()

    const dateFrom = sanitizeYMD(currentModel.dateFrom)
    const dateTo = sanitizeYMD(currentModel.dateTo)
    const md = currentModel._metaData

    if (md?.preset != null) {
      const pv = Number(md.preset)
      if (Number.isFinite(pv) && pv > 0) {
        if (PRESETS.includes(pv as typeof PRESETS[number])) {
          setMode('preset')
          setPresetDays(pv)
          setNDays('')
          setFrom('')
          setTo('')
        } else {
          setMode('nDays')
          setPresetDays(null)
          setNDays(String(pv))
          setFrom('')
          setTo('')
        }
        return
      }
    }

    if (md?.customRange && dateFrom && dateTo) {
      setMode('range')
      setPresetDays(null)
      setNDays('')
      setFrom(dateFrom)
      setTo(dateTo)
      return
    }

    resetPanel()
  }

  function buildModelAndText() {
    // Validation
    if (mode === 'nDays' && nDays) {
      const n = parseInt(nDays, 10)
      if (!Number.isFinite(n) || n <= 0) return { error: 'Enter a valid positive number of days.' }
      if (n > 365) return { error: 'Maximum allowed range is 365 days.' }
    }
    if (mode === 'range') {
      const f = sanitizeYMD(from),
        t = sanitizeYMD(to)
      if (f && f > todayYMD) return { error: 'From date cannot be in the future.' }
      if (t && t > todayYMD) return { error: 'To date cannot be in the future.' }
      if (f && t && f > t) return { error: '"From" date cannot be after "To" date.' }
      if (f && t) {
        const diff = Math.floor((+parseYMDLocal(t) - +parseYMDLocal(f)) / DAY_MS) + 1
        if (diff > 365) return { error: 'Maximum allowed range is 365 days.' }
      }
    }
    if (mode === 'preset' && presetDays && presetDays > 365) {
      return { error: 'Maximum allowed range is 365 days.' }
    }

    // Build
    if (mode === 'preset' && presetDays) {
      const r = subtractDaysWindowLocal(presetDays)
      return {
        model: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: r.from,
          dateTo: r.to,
          _metaData: { preset: presetDays },
        },
        text: `Last ${presetDays} days`,
      }
    }
    if (mode === 'nDays' && nDays) {
      const n = parseInt(nDays, 10)
      const r = subtractDaysWindowLocal(n)
      return {
        model: { filterType: 'date', type: 'inRange', dateFrom: r.from, dateTo: r.to, _metaData: { preset: n } },
        text: `Last ${n} days`,
      }
    }
    if (mode === 'range') {
      const f = sanitizeYMD(from),
        t = sanitizeYMD(to)
      if (f && t && f <= t) {
        return {
          model: { filterType: 'date', type: 'inRange', dateFrom: f, dateTo: t, _metaData: { customRange: true } },
          text: `${formatDateTime(f)} → ${formatDateTime(t)}`,
        }
      }
    }
    return { model: null, text: '' }
  }
  // -----------------------------

  const activePreset = useMemo(() => {
    if (mode === 'preset' && presetDays) return presetDays
    if (mode === 'nDays' && nDays) return Number(nDays) || null
    if (externalPreset && Number.isFinite(externalPreset)) return externalPreset
    return null
  }, [mode, presetDays, nDays, externalPreset])

  const customActive = useMemo(() => mode === 'range' || (!mode && externalCustom), [mode, externalCustom])

  const {
    model: builtModel,
    text,
    error,
  } = useMemo(() => {
    const res = buildModelAndText()
    return { model: res.model ?? null, text: (res as any).text ?? '', error: (res as any).error ?? null }
  }, [mode, presetDays, nDays, from, to, todayYMD])

  return (
    <QuickDateContainer ref={hostRef}>
      <FieldLabel>{label}</FieldLabel>
      <QuickDateBox
        hasValue={!!displayText}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <FaCalendarAlt />
        <span>{displayText || 'Select…'}</span>
        <FaChevronDown />
      </QuickDateBox>

      {open && (
        <QuickDatePanel role="dialog" aria-label="Last modified picker">
          <QuickDatePanelTitle>Choose period</QuickDatePanelTitle>

          <PresetButtonsRow>
            {PRESETS.map((d) => {
              const isActive = activePreset === d
              return (
                <PresetButton
                  key={d}
                  type="button"
                  isActive={!!isActive}
                  aria-pressed={!!isActive}
                  onClick={() => {
                    setMode('preset')
                    setPresetDays(d)
                    setNDays('')
                    setFrom('')
                    setTo('')
                  }}
                  title={`Last ${d} days`}
                >
                  Last {d} days
                </PresetButton>
              )
            })}

            <CustomInputRow>
              <NumberInput
                value={
                  mode === 'nDays'
                    ? nDays
                    : externalPreset && !PRESETS.includes(externalPreset as any) && externalPreset > 0
                    ? String(externalPreset)
                    : ''
                }
                isActive={
                  (mode === 'nDays' && !!nDays) ||
                  (!!externalPreset && !PRESETS.includes(externalPreset as any) && externalPreset > 0)
                }
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '')
                  setNDays(value)
                  if (value) {
                    setMode('nDays')
                    setPresetDays(null)
                    setFrom('')
                    setTo('')
                  } else {
                    setMode(null)
                    setPresetDays(null)
                    setFrom('')
                    setTo('')
                  }
                }}
                placeholder="Enter Last n days"
                inputMode="numeric"
                aria-label="Last n days"
              />
            </CustomInputRow>
          </PresetButtonsRow>

          <CustomRangeSectionTitle isActive={customActive}>Custom date range</CustomRangeSectionTitle>
          <DateRangePanelRow>
            <DateInputGroup>
              <span>From</span>
              <DateInput
                type="date"
                max={todayYMD}
                value={mode === 'range' ? sanitizeYMD(from) : externalCustom ? sanitizeYMD(currentModel?.dateFrom) : ''}
                onChange={(e) => {
                  setMode('range')
                  setPresetDays(null)
                  setNDays('')
                  setFrom(sanitizeYMD(e.target.value))
                }}
              />
            </DateInputGroup>
            <DateInputGroup>
              <span>To</span>
              <DateInput
                type="date"
                max={todayYMD}
                value={mode === 'range' ? sanitizeYMD(to) : externalCustom ? sanitizeYMD(currentModel?.dateTo) : ''}
                onChange={(e) => {
                  setMode('range')
                  setPresetDays(null)
                  setNDays('')
                  setTo(sanitizeYMD(e.target.value))
                }}
              />
            </DateInputGroup>
          </DateRangePanelRow>

          {error && <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>{error}</div>}

          <QuickDateActions>
            <ActionButton type="button" onClick={() => setOpen(false)}>
              Cancel
            </ActionButton>

            <ActionButton
              type="button"
              isPrimary
              onClick={() => {
                if (!error) {
                  onCommit({ model: builtModel, text })
                  setOpen(false)
                }
              }}
              disabled={!!error || !builtModel}
            >
              Apply
            </ActionButton>
          </QuickDateActions>
        </QuickDatePanel>
      )}
    </QuickDateContainer>
  )
}

/* ---------- Styled ---------- */
const QuickDateContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1.3 1 190px;
  min-width: 190px;
  position: relative;
  overflow: visible;
`
const FieldLabel = styled.label`
  ${({ theme }) => theme.typography.s.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xxxs};
`
const QuickDateBox = styled.div<{ hasValue: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxxs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xxxs};
  height: 32px;
  border: 1px solid ${({ theme, hasValue }) => (hasValue ? theme.colors.primary.dark : theme.colors.grey.light)};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  ${({ theme }) => theme.typography.xs.normal};
  background: ${({ theme }) => theme.colors.neutral.lightest};
  cursor: pointer;
  span {
    color: ${({ theme, hasValue }) => (hasValue ? theme.colors.grey.darker : theme.colors.grey.neutral)};
  }
  svg:last-child {
    margin-left: auto;
    font-size: 12px;
    opacity: 0.8;
  }
`
const QuickDatePanel = styled.div`
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  z-index: 2100;
  min-width: 320px;
  background: ${({ theme }) => theme.colors.neutral.lightest};
  border: 1px solid ${({ theme }) => theme.colors.neutral.light};
  border-radius: ${({ theme }) => theme.spacing.xxs};
  box-shadow: ${({ theme }) => theme.shadows.light};
  padding: ${({ theme }) => theme.spacing.xs};
`
const QuickDatePanelTitle = styled.div`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  ${({ theme }) => theme.typography.xs.bold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`
const PresetButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.xxs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`
const PresetButton = styled.button<{ isActive?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.xs};
  height: 32px;
  ${({ theme }) => theme.typography.xs.normal};
  border: 2px solid ${({ theme, isActive }) => (isActive ? theme.colors.primary.neutral : theme.colors.neutral.light)};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  background: ${({ theme, isActive }) => (isActive ? theme.colors.primary.neutral : theme.colors.neutral.lightest)};
  color: ${({ theme, isActive }) => (isActive ? theme.colors.neutral.lightest : theme.colors.grey.darker)};
  font-weight: ${({ theme, isActive }) => (isActive ? theme.fontWeight.semiBold : theme.fontWeight.normal)};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    opacity: 0.9;
    background: ${({ theme, isActive }) => (isActive ? theme.colors.primary.neutral : theme.colors.neutral.light)};
  }
  &:active {
    transform: scale(0.98);
  }
`
const CustomInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`
const CustomRangeSectionTitle = styled.div<{ isActive?: boolean }>`
  ${({ theme }) => theme.typography.xs.medium};
  margin-bottom: ${({ theme }) => theme.spacing.xxxs};
  color: ${({ theme, isActive }) => (isActive ? theme.colors.primary.dark : theme.colors.grey.darker)};
`
const NumberInput = styled.input<{ isActive?: boolean }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xxs};
  height: 32px;
  width: 142px;
  border: 2px solid ${({ theme, isActive }) => (isActive ? theme.colors.primary.neutral : theme.colors.grey.light)};
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  ${({ theme }) => theme.typography.xs.normal};
  background: ${({ theme, isActive }) => (isActive ? theme.colors.primary.lightest : theme.colors.neutral.lightest)};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.neutral};
  }
`
const QuickDateActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.xxs};
  margin-top: ${({ theme }) => theme.spacing.xs};
`
